import { useCallback, useMemo, useState } from 'react'
import {
    CalendarDay,
    generateTimeSlots,
    getEffectiveHours,
    getEventsForDay,
    getEventIntervals,
    buildGridSegments,
    getGridHeight,
    createMinutesToPixels,
    layoutDayEvents,
    formatWeekDay,
    formatDate,
} from '../../utils/calendar-utils'
import { AgendaEvent } from '@/lib/api/common/interfaces/agenda.interface'
import { EventCard } from '../events/EventCard'
import { DayGrid, TimeRuler } from './TimeSlot'

interface DayViewProps {
    day: CalendarDay
    events: AgendaEvent[]
    workingHours?: { start: number; end: number }
    slotDuration?: number
    onSlotClick?: (date: Date) => void
    onEventClick?: (event: AgendaEvent) => void
}

export function DayView({
    day,
    events,
    workingHours = { start: 8, end: 18 },
    slotDuration = 30,
    onSlotClick,
    onEventClick,
}: DayViewProps) {
    // Faixas vazias que o operador abriu na mão; a chave é o intervalo, então
    // uma faixa aberta continua aberta enquanto o dia não muda de forma.
    const [expandedGaps, setExpandedGaps] = useState<ReadonlySet<string>>(
        new Set()
    )

    const handleExpandGap = useCallback((key: string) => {
        setExpandedGaps(previous => new Set(previous).add(key))
    }, [])

    const handleCollapseGap = useCallback((key: string) => {
        setExpandedGaps(previous => {
            const next = new Set(previous)
            next.delete(key)
            return next
        })
    }, [])

    const dayEvents = useMemo(
        () => getEventsForDay(events, day.date),
        [events, day.date]
    )

    const hours = useMemo(
        () => getEffectiveHours(dayEvents, workingHours),
        [dayEvents, workingHours]
    )

    const timeSlots = useMemo(
        () => generateTimeSlots(day.date, hours.start, hours.end, slotDuration),
        [day.date, hours, slotDuration]
    )

    const segments = useMemo(
        () =>
            buildGridSegments(
                timeSlots,
                getEventIntervals(dayEvents, hours, slotDuration),
                slotDuration,
                expandedGaps
            ),
        [timeSlots, dayEvents, hours, slotDuration, expandedGaps]
    )

    const positionedEvents = useMemo(
        () =>
            layoutDayEvents(
                dayEvents,
                hours,
                slotDuration,
                createMinutesToPixels(segments, slotDuration)
            ),
        [dayEvents, hours, slotDuration, segments]
    )

    const handleSlotClick = (time: Date) => {
        if (day.isPast) return
        if (onSlotClick) {
            const slotDate = new Date(day.date)
            slotDate.setHours(time.getHours(), time.getMinutes(), 0, 0)
            onSlotClick(slotDate)
        }
    }

    return (
        <div className="flex flex-col h-full overflow-hidden">
            <div className="flex border-b border-base-300">
                <div className="w-16 flex-shrink-0 border-r border-base-300"></div>
                <div
                    className={`flex-1 px-4 py-3 ${
                        day.isToday ? 'bg-secondary/10' : ''
                    }`}
                >
                    <div className="text-sm text-base-content/60 capitalize">
                        {formatWeekDay(day.date)}
                    </div>
                    <div
                        className={`text-2xl font-semibold ${
                            day.isToday
                                ? 'text-secondary'
                                : 'text-base-content'
                        }`}
                    >
                        {formatDate(day.date, 'dd MMMM')}
                    </div>
                </div>
            </div>

            <div className="flex-1 overflow-y-auto calendar-scroll">
                <div className="flex min-h-full">
                    <TimeRuler segments={segments} />
                    <div
                        className={`flex-1 relative ${
                            day.isToday ? 'bg-secondary/5' : ''
                        }`}
                        style={{ height: `${getGridHeight(segments)}px` }}
                    >
                        <DayGrid
                            segments={segments}
                            isPast={day.isPast}
                            onSlotClick={handleSlotClick}
                            onExpandGap={handleExpandGap}
                            onCollapseGap={handleCollapseGap}
                        />

                        <div className="absolute inset-0 pointer-events-none">
                            {positionedEvents.map(
                                ({ event, top, height, left, width, columnCount }) => (
                                    <div
                                        key={event.id}
                                        className="absolute px-1 pointer-events-auto"
                                        style={{
                                            top: `${top}px`,
                                            height: `${height}px`,
                                            left: `${left}%`,
                                            width: `${width}%`,
                                        }}
                                    >
                                        <div className="h-full overflow-hidden">
                                            <EventCard
                                                event={event}
                                                onClick={onEventClick}
                                                compact={height < 44 || columnCount > 3}
                                                showTime={height >= 92}
                                                showDetails={height >= 118}
                                            />
                                        </div>
                                    </div>
                                )
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}
